import { OrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { useCallback, useMemo, useState } from 'react';

export interface ConvertQuoteGroup {
  name: string;
  packs: OrderGuideItem[];
  partitionUUID: string;
}

/** A group enriched with its live selection + expansion state for the modal. */
export interface ConvertQuoteGroupView extends ConvertQuoteGroup {
  checkedState: boolean | 'indeterminate';
  isExpanded: boolean;
  selectedCount: number;
  total: number;
}

interface ConvertToPriceQuoteSelection {
  groupViews: ConvertQuoteGroupView[];
  isPackSelected: (partitionUUID: string, variantPackUUID: string) => boolean;
  selectedVariantPackUUIDs: string[];
  toggleGroup: (partitionUUID: string) => void;
  toggleGroupExpanded: (partitionUUID: string) => void;
  togglePack: (partitionUUID: string, variantPackUUID: string) => void;
  totalSelected: number;
}

/**
 * A pack can appear in multiple partitions, so selection is keyed by the
 * composite (partition, pack) identity rather than the pack UUID alone —
 * otherwise toggling an occurrence in one group would flip it in every group.
 */
const rowKey = (partitionUUID: string, variantPackUUID: string) =>
  `${partitionUUID}::${variantPackUUID}`;

export const useConvertToPriceQuoteSelection = (
  groups: ConvertQuoteGroup[],
): ConvertToPriceQuoteSelection => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const togglePack = useCallback(
    (partitionUUID: string, variantPackUUID: string) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const key = rowKey(partitionUUID, variantPackUUID);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [],
  );

  const toggleGroup = useCallback(
    (partitionUUID: string) => {
      const group = groups.find((g) => g.partitionUUID === partitionUUID);
      if (!group) {
        return;
      }
      setSelected((prev) => {
        const next = new Set(prev);
        const allSelected = group.packs.every((p) =>
          next.has(rowKey(partitionUUID, p.variantPackUUID)),
        );
        group.packs.forEach((p) => {
          const key = rowKey(partitionUUID, p.variantPackUUID);
          if (allSelected) {
            next.delete(key);
          } else {
            next.add(key);
          }
        });
        return next;
      });
    },
    [groups],
  );

  const toggleGroupExpanded = useCallback((partitionUUID: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(partitionUUID)) {
        next.delete(partitionUUID);
      } else {
        next.add(partitionUUID);
      }
      return next;
    });
  }, []);

  const groupViews = useMemo<ConvertQuoteGroupView[]>(
    () =>
      groups.map((group) => {
        const selectedCount = group.packs.filter((p) =>
          selected.has(rowKey(group.partitionUUID, p.variantPackUUID)),
        ).length;
        const total = group.packs.length;
        return {
          ...group,
          checkedState:
            selectedCount === 0
              ? false
              : selectedCount === total
                ? true
                : 'indeterminate',
          isExpanded: !collapsed.has(group.partitionUUID),
          selectedCount,
          total,
        };
      }),
    [groups, selected, collapsed],
  );

  // A pack can be selected in multiple partitions but is only converted once,
  // so the API payload and the "Convert N items" count use unique UUIDs.
  const selectedVariantPackUUIDs = useMemo(
    () => [
      ...new Set(
        groups.flatMap((group) =>
          group.packs
            .filter((p) =>
              selected.has(rowKey(group.partitionUUID, p.variantPackUUID)),
            )
            .map((p) => p.variantPackUUID),
        ),
      ),
    ],
    [groups, selected],
  );

  const isPackSelected = useCallback(
    (partitionUUID: string, variantPackUUID: string) =>
      selected.has(rowKey(partitionUUID, variantPackUUID)),
    [selected],
  );

  return {
    groupViews,
    isPackSelected,
    selectedVariantPackUUIDs,
    toggleGroup,
    toggleGroupExpanded,
    togglePack,
    totalSelected: selectedVariantPackUUIDs.length,
  };
};
